import {
  treeFormationName,
  type InventoryTypeCurveTreeFormation,
} from 'types/api-responses';

/**
 * Formation swatch colours for the Type Curves tree, matching the Inventory
 * Workspace mock's strat-column palette. Formations the mock names get their
 * exact colour; anything else resolves deterministically from the fallback
 * palette by a stable hash, so a formation keeps its colour across renders
 * and workspaces.
 */
const KNOWN_FORMATION_COLORS: Record<string, string> = {
  USS: '#c5ba81',
  MSS: '#899d9d',
  JMS: '#44a4a4',
  LSS: '#9b9da0',
  Dean: '#9d4041',
};

const FALLBACK_COLORS = [
  '#c5ba81',
  '#899d9d',
  '#44a4a4',
  '#9b9da0',
  '#9d4041',
  '#7d6a94',
  '#5c8a5c',
  '#b08968',
];

/**
 * The swatch colour for a formation.
 * @param {string} formationName - The formation's display name.
 * @return {string} Hex colour for the tree's square swatch.
 */
export const colorForFormation = (formationName: string): string => {
  const known = KNOWN_FORMATION_COLORS[formationName];
  if (known) return known;

  let hash = 0;
  for (let i = 0; i < formationName.length; i += 1) {
    hash = (hash * 31 + formationName.charCodeAt(i)) % 997;
  }
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
};

/**
 * The swatch colour for a tree formation node: the backend-configured `color`
 * when present, else the local deterministic fallback. The single source of
 * truth used by the sidebar tree, the Well-Filter / Color-by formation options
 * and the Color-by default colour, so all three stay in sync.
 * @param {InventoryTypeCurveTreeFormation} formation - The tree formation node.
 * @return {string} Hex colour for the formation's swatch.
 */
export const formationSwatchColor = (
  formation: InventoryTypeCurveTreeFormation
): string => formation.color ?? colorForFormation(treeFormationName(formation));
